// Steps 3–7 as one pure evaluation: grid → cell values → patterns → payout (docs/04 §2, §4).
// Used for real spins, Fate dry-runs, nudge previews and the odds Monte Carlo.
import { B } from '../content/balance';
import { computeCellValues, makeGridView, type Pending, type ValueStep } from './effects';
import { D, Decimal, ONE } from './decimal';
import { deckIndex, dims, visibleUids } from './grid';
import type { Modifiers } from './modifiers';
import { moodOf, moodXMults } from './hunger';
import { activeCharms, type Modifiers as Mods } from './modifiers';
import { evaluatePatterns } from './patterns';
import { devourCheck, type DevourResult } from './doom';
import { CHARMS } from '../content/registry';
import type { BonusMode, Cell, PatternHit, RunState, WinTier } from './types';
import type { GridView } from '../content/defs';

export interface EvalOpts {
  spinIndex: number;
  held: boolean[];
  perfect: boolean;
  /** Luck over the Fate cap, converted to ×MULT. */
  luckOverCap: number;
}

export interface Evaluation {
  uids: number[][];
  grid: string[][];
  view: GridView;
  values: number[][];
  steps: ValueStep[][][];
  hits: PatternHit[];
  off: { cell: Cell; value: number }[];
  base: Decimal;
  xMults: { source: string; x: number }[];
  payout: Decimal;
  pending: Pending[];
  guarded: Set<number>;
  sigils: Partial<Record<BonusMode, number>>;
  gorge: boolean;
  devour: DevourResult;
}

export function evaluate(run: RunState, mods: Modifiers, stops: number[], opts: EvalOpts): Evaluation {
  const { rows } = dims(run.size);
  const uids = visibleUids(run, stops);
  const deck = deckIndex(run);
  // Eaten instances are gone from the grid: their cells pay 0 and join no pattern.
  const live = run.eaten.length ? new Map([...deck].filter(([uid]) => !run.eaten.includes(uid))) : deck;
  const view = makeGridView(uids, live, rows);
  const { values, steps, pending, guarded } = computeCellValues(view, mods, {
    seed: run.seed,
    spinIndex: opts.spinIndex,
    held: opts.held,
    hunger: run.hunger,
    doom: run.doom ?? 0,
  });
  const hits = evaluatePatterns(view, values, run.size, mods, run.hymns, { spinIndex: opts.spinIndex, held: opts.held });
  if (hits.some((h) => h.cleanse)) pending.push({ kind: 'cleanse' });

  const inHit = new Set<string>();
  for (const h of hits) for (const c of h.cells) inHit.add(`${c.reel},${c.row}`);

  // Step 6+: post-pattern hooks (e.g. Key in a paying pattern).
  for (const v of view.all())
    v.def.post?.({
      self: v,
      inPayingPattern: inHit.has(`${v.cell.reel},${v.cell.row}`),
      grantFreeCharm: () => pending.push({ kind: 'freeCharm' }),
      hungerLater: (n) => pending.push({ kind: 'hunger', n }),
    });

  const off: { cell: Cell; value: number }[] = [];
  const sigils: Partial<Record<BonusMode, number>> = {};
  for (const v of view.all()) {
    if (v.def.sigil) sigils[v.def.sigil] = (sigils[v.def.sigil] ?? 0) + 1;
    if (!v.def.offPattern || mods.offPatternOff || inHit.has(`${v.cell.reel},${v.cell.row}`)) continue;
    const value = values[v.cell.reel]![v.cell.row]!;
    if (value > 0) off.push({ cell: v.cell, value });
  }

  let base = D(0);
  for (const h of hits) base = base.add(h.value);
  for (const o of off) base = base.add(o.value);

  const xMults = spinXMults(run, mods, view, opts);
  let x = ONE;
  for (const m of xMults) x = x.mul(m.x);
  // Step 8: DOOM — a Devour takes the payout (or doubles down on it: Golden Tooth, MIRACLE).
  const devour = devourCheck(run, mods, view, hits, opts.spinIndex);
  const payout = base.mul(x).mul(devour.x).floor();

  return {
    uids,
    grid: uids.map((col) => col.map((uid) => deck.get(uid)?.defId ?? 'cherry')),
    view,
    values,
    steps,
    hits,
    off,
    base,
    xMults,
    payout,
    pending,
    guarded,
    sigils,
    gorge: hits.some((h) => h.pattern === 'gorge'),
    devour,
  };
}

/** Every ×MULT source for this spin, after Midas (+0.1 each) and the Taxman (square roots). */
export function spinXMults(run: RunState, mods: Mods, view: GridView, opts: EvalOpts): { source: string; x: number }[] {
  const list = [...mods.xMult, ...moodXMults(run, mods)];
  if (opts.perfect && mods.perfectPullXMult !== 1) list.push({ source: 'perfect', x: mods.perfectPullXMult });
  if (opts.luckOverCap > 0) list.push({ source: 'luck', x: 1 + opts.luckOverCap * B.LUCK_OVERCAP_XMULT });
  const mood = moodOf(run, mods);
  for (const id of activeCharms(run, mods.charmSlots)) {
    const x = CHARMS.get(id)?.evalXMult?.({ grid: view, spinIndex: opts.spinIndex, mood });
    if (x !== undefined && x !== 1) list.push({ source: id, x });
  }
  if (mods.forcedDeadEvery > 0 && run.stats.spins > 0 && run.stats.spins % mods.forcedDeadEvery === 0) list.push({ source: 'p_borrowedluck', x: 0 });
  const devours = run.window.counters.devours ?? 0;
  if (mods.devourXMult > 0 && devours > 0) list.push({ source: 'jarofteeth', x: 1 + mods.devourXMult * devours });
  return list.map((m) => {
    let x = m.x;
    if (mods.midas && x > 1) x += mods.midas;
    if (mods.sqrtXMult && x > 0) x = Math.sqrt(x);
    return x === m.x ? m : { source: m.source, x };
  });
}

export function winTier(payout: Decimal, par: Decimal, gorge: boolean): WinTier {
  if (gorge && payout.gt(0)) return 'gorge';
  if (payout.lte(0)) return 'none';
  const ratio = par.gt(0) ? payout.div(par).toNumber() : 0;
  if (ratio >= B.TIERS.legendary) return 'legendary';
  if (ratio >= B.TIERS.epic) return 'epic';
  if (ratio >= B.TIERS.mega) return 'mega';
  if (ratio >= B.TIERS.big) return 'big';
  return 'win';
}

export const TIER_ORDER: WinTier[] = ['none', 'win', 'big', 'mega', 'epic', 'legendary', 'gorge'];
export function tierAtLeast(t: WinTier, min: WinTier): boolean {
  return TIER_ORDER.indexOf(t) >= TIER_ORDER.indexOf(min);
}
